
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, Mail, Users, Calendar } from "lucide-react";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";
import { useTranslation } from "react-i18next";

export const NotificationSettings = () => {
  const { settings, loading, updateSettings } = useNotificationSettings();
  const { t } = useTranslation('profile');

  if (loading || !settings) {
    return (
      <Card className="uplivion-card">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const notificationTypes = [
    {
      key: 'timeline_entries',
      title: 'Timeline-Einträge & Verwaltete Profile',
      description: 'Benachrichtigungen über neue Timeline-Einträge und Aktivitäten auf verwalteten Profilen',
      icon: Calendar,
      emailKey: 'timeline_entries_email' as const,
      appKey: 'timeline_entries_app' as const
    },
    {
      key: 'friend_requests',
      title: 'Freundschaftsanfragen & Bestätigungen',
      description: 'Benachrichtigungen über neue Freundschaftsanfragen und deren Annahme',
      icon: Users,
      emailKey: 'friend_requests_email' as const,
      appKey: 'friend_requests_app' as const
    },
    {
      key: 'birthdays',
      title: 'Geburtstage',
      description: 'Erinnerungen an Geburtstage von Freunden',
      icon: Bell,
      emailKey: 'birthdays_email' as const,
      appKey: 'birthdays_app' as const
    }
  ];

  return (
    <Card className="uplivion-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Benachrichtigungseinstellungen
        </CardTitle>
        <CardDescription>
          Verwalten Sie, wie Sie über Aktivitäten benachrichtigt werden möchten
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {notificationTypes.map((type) => {
          const Icon = type.icon;
          return (
            <div key={type.key} className="space-y-4">
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-muted-foreground" />
                <div>
                  <h3 className="font-medium">{type.title}</h3>
                  <p className="text-sm text-muted-foreground">{type.description}</p>
                </div>
              </div>
              
              <div className="ml-8 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <Label htmlFor={`${type.key}-email`}>E-Mail</Label>
                  </div>
                  <Switch
                    id={`${type.key}-email`}
                    checked={settings[type.emailKey]}
                    onCheckedChange={(checked) => 
                      updateSettings({ [type.emailKey]: checked })
                    }
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-muted-foreground" />
                    <Label htmlFor={`${type.key}-app`}>App-Benachrichtigung</Label>
                  </div>
                  <Switch
                    id={`${type.key}-app`}
                    checked={settings[type.appKey]}
                    onCheckedChange={(checked) => 
                      updateSettings({ [type.appKey]: checked })
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
